import { KeyboardEvent, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useSelector } from 'react-redux'
import styled, { useTheme } from 'styled-components'
import { ImageAsset } from '@/components/common'
import { Typo } from '@/components/common/typography'
import { useGetDeliveryAddressList } from '@/components/domains/delivery-addresses/hooks/use-get-delivery-address-list'
import { ModalShippingAddressSelect } from '@/components/domains/order2/modal-shipping-address-select'
import { GlobalColorEnum } from '@/constants/global-color.enum'
import { SHIPPING_REQUEST_DIRECT_INPUT_ID } from '@/constants/legacy/constData'
import ROUTES from '@/constants/legacy/constRoutes'
import { useCustomRouter, useModal } from '@/containers/hooks'
import { OrderFormType } from '@/containers/hooks/order/order-form-schema'
import { useSetEstimateOrder } from '@/containers/order/set-estimate-order.hook'
import { RootState } from '@/stores/store'
import { Base64ConverterUtil } from '@/utils/base64-converter.util'
import { formatPhoneNumber } from '@/utils/utilString'

interface ShippingRequestItem {
  id: number
  message: string
}

export const ShippingAddressSection = () => {
  const { setValue } = useFormContext<OrderFormType>()

  const { color } = useTheme()
  const { push } = useCustomRouter()
  const { useModalProps } = useModal()
  const { setEstimateOrderByCustomerTemplateAddress } = useSetEstimateOrder()

  const [selectedShippingRequest, setSelectedShippingRequest] = useState<ShippingRequestItem | undefined>(undefined)

  // 배송지 목록에서 선택한 배송지 정보
  const orderCustomerAddressRedux = useSelector(
    (rootState: RootState) => rootState.reduxDataReducers.customerAddressForOrder
  )

  // 기본 배송지 정보
  const { defaultAddress } = useGetDeliveryAddressList()

  const selectedAddress = orderCustomerAddressRedux || defaultAddress

  const handleSelectShippingRequest = (shippingRequestItem: ShippingRequestItem) => {
    setSelectedShippingRequest(shippingRequestItem)
    setValue('customerAddress.message', shippingRequestItem.message)
  }

  const handleDirectShippingRequestChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue('customerAddress.message', e.target.value)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
    }
  }

  useEffect(() => {
    if (!selectedAddress) {
      return
    }
    setValue('customerAddress', {
      basicAddress: selectedAddress.basicAddress,
      customerTemplateAddress: selectedAddress.id,
      detailedAddress: selectedAddress.detailedAddress?.toString() || '',
      isDefault: selectedAddress.isDefault,
      message: selectedAddress.message || '',
      mobileNumber: selectedAddress.mobileNumber || '',
      name: selectedAddress.name,
      postNumber: selectedAddress.postNumber,
      templateName: selectedAddress.templateName,
    })

    const customerTemplateAddressId = Base64ConverterUtil.convertBase64ToNumberId(selectedAddress.id)
    if (!Number.isNaN(customerTemplateAddressId)) {
      setEstimateOrderByCustomerTemplateAddress(customerTemplateAddressId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAddress])

  return (
    <>
      <StyledSectionTitle>
        <Typo variant="Body1 Bold">배송지 정보</Typo>
        <StyledButton
          type="button"
          onClick={() => {
            push(selectedAddress ? ROUTES.ORDER.DELIVERY_ADDRESSES : ROUTES.DELIVERY_ADDRESSES.CREATE)
          }}
        >
          <Typo variant="Body2 Medium">{!!selectedAddress ? '변경' : '추가'}</Typo>
        </StyledButton>
      </StyledSectionTitle>
      <StyledAddressContainer>
        {!!selectedAddress ? (
          <>
            <div>
              <Typo variant="Body3 Regular">{`${selectedAddress.name} ${
                selectedAddress.isDefault ? '(기본 배송지)' : ''
              }`}</Typo>
              <br />
              <Typo variant="Body3 Regular">{formatPhoneNumber(selectedAddress.mobileNumber || '')}</Typo>
              <br />
              <Typo variant="Body3 Regular">{`${selectedAddress.basicAddress} ${selectedAddress.detailedAddress}`}</Typo>
            </div>
            <StyledSelectBox onClick={() => useModalProps.showModal()}>
              {selectedShippingRequest ? (
                <Typo variant="Body1 regular">{selectedShippingRequest.message}</Typo>
              ) : (
                <Typo variant="Body1 regular" color={color.gray300}>
                  배송 요청사항을 선택해 주세요.
                </Typo>
              )}
              <ImageAsset name="ic_chevron_down_gray700_24" size={24} svg />
            </StyledSelectBox>
            {selectedShippingRequest?.id === SHIPPING_REQUEST_DIRECT_INPUT_ID && (
              <StyledInput onChange={handleDirectShippingRequestChange} onKeyDown={handleKeyDown} maxLength={60} />
            )}
          </>
        ) : (
          <div>
            <Typo variant="Body3 Regular">기본 배송 정보가 없습니다.</Typo>
            <br />
            <Typo variant="Body3 Regular">배송지를 추가해 주세요.</Typo>
          </div>
        )}
      </StyledAddressContainer>
      <ModalShippingAddressSelect
        title="배송 요청 사항"
        useModalProps={useModalProps}
        selectedShippingRequest={selectedShippingRequest}
        onSelectedShippingRequestChange={handleSelectShippingRequest}
      />
    </>
  )
}

const StyledSectionTitle = styled.div`
  padding: 1.2rem 1.6rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const StyledButton = styled.button`
  background-color: transparent;
  border: 1px solid ${GlobalColorEnum.Gray200};
  border-radius: 0.8rem;
  padding: 0.75rem 1.4rem;
  cursor: pointer;
`

const StyledAddressContainer = styled.div`
  padding: 1.6rem 1.6rem 2rem;
`

const StyledSelectBox = styled.div`
  margin-top: 1.6rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 5rem;
  border: 1px solid ${GlobalColorEnum.Gray100};
  border-radius: 0.8rem;
  color: ${(props) => props.theme.color.gray300};
  box-sizing: border-box;
  padding: 1.6rem;
  cursor: pointer;
`

const StyledInput = styled.input`
  margin-top: 1rem;
  width: 100%;
  padding: 1rem;
  background-color: ${(props) => props.theme.color.gray50};
  border: none;
  border-radius: 0.8rem;
  :focus {
    outline: none;
  }
`
